// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface LenderData {
  lender: string;
  value: number;
  percentage: number;
}

interface LenderPieChartProps {
  data: LenderData[];
  title: string;
  color?: string;
  formatValue?: (value: number) => string;
}

// Professional color palette for lenders
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

const LenderPieChart: React.FC<LenderPieChartProps> = ({
  data,
  title,
  color = '#3b82f6',
  formatValue = (value) => `$${(value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}) => {
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2">{data.lender}</p>
          <p style={{ color: payload[0].payload.fill }}>
            Value: {formatValue(data.value)}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {data.percentage.toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percentage }: any) => {
    // Only show label if percentage is >= 5%
    if (percentage < 5) return null;

    const RADIAN = Math.PI / 180;
    // Position label outside the pie chart
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#9CA3AF"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={13}
        fontWeight="500"
      >
        {`${percentage.toFixed(1)}%`}
      </text>
    );
  };

  const CustomLegend = ({ payload }: any) => {
    if (!payload || payload.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-3 justify-center mt-4">
        {payload.map((entry: any, index: number) => {
          const isTopLender = index === 0 && entry.value !== 'Other';
          return (
            <div
              key={`legend-${index}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                isTopLender ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-gray-800'
              }`}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-white font-medium">
                {entry.value}
                {isTopLender && (
                  <span className="ml-1.5 text-xs text-emerald-400 font-semibold">
                    (Top)
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-400 ml-1">
                {entry.payload?.percentage?.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
      />

      <h3 className="font-bold text-lg mb-4 text-white relative z-10">{title}</h3>

      {data.length === 0 ? (
        <div className="p-8 bg-gray-700 rounded text-center">
          <p className="text-gray-400">No lender data available for the selected period.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              nameKey="lender"
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#1F2937"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default LenderPieChart;
